import React, { useEffect, useState } from 'react';
import { Box, Button, Skeleton, SxProps, Tooltip, Typography, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Accordion, useModal } from '@europrocurement/flexy-components';
import {
    DOC_TYPE_DOWNLOADABLE,
    DOC_TYPE_INTERNAL,
    DOC_TYPE_REQUIRED,
    type DocTypeEnumType,
    DossierOpenSearch,
    findNameByDocTypeId,
} from '@europrocurement/l2d-domain';
import useDossierFile from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierFile';
import { type DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import {
    useCurrentDossierCtx,
    useDocumentPreviewContext,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import {
    dossiersBasePath,
    LAYOUT_DOCUMENTS_FULL,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faPlus } from '@fortawesome/sharp-regular-svg-icons';
import {
    ADD_DOS_DOC_CTX,
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
    useTypeDocumentsCtx,
} from '@europrocurement/l2d-modules/providers';
import {
    type DocumentExtension,
    useDossierDocumentFilesApi,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { Link } from 'react-router-dom';
import { CATEGORY_LEGAL_ANNOUCEMENT } from '@europrocurement/l2d-modules/modules/Dossiers/constants/articlesCategories';
import { VALIDATOR_TYPE_LEGAL_ANNOUCEMENT } from '@europrocurement/l2d-modules/modules/Dossiers/constants/validators';
import useDossierArticle from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierArticle';
import useDossierProcessesActionsAndRender from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierProcessesActionsAndRender';
import RequiredDocumentsList from '../../RequiredDocumentsList/RequiredDocumentsList';
import RequiredDocumentUploader from '../../RequiredDocumentUploader/RequiredDocumentUploader';
import { type DocumentTypeItem, DocumentTypeForm } from '../../../shared';
import DownloadableDocumentsList from '../../DownloadableDocumentsList/DownloadableDocumentsList';

export type DossierDocumentsValidationListProps = {
    displayList: boolean;
    currentLayout?: string;
};

const DossierDocumentsValidationList: React.FunctionComponent<DossierDocumentsValidationListProps> =
    function (props) {
        const { displayList, currentLayout } = props;
        const { siblings } = useCurrentDossierCtx();
        const [displayedDocType, setDisplayedDocType] =
            useState<DocTypeEnumType>(DOC_TYPE_REQUIRED);
        const [listItems, setListItems] = useState<DisplayedDocument[]>([]);
        const [externalDocsByDossier, setExternalDocsByDossier] = useState<
            Record<number, DisplayedDocument[]>
        >({});
        const { loadLegalAnnoucementsJustifs } = useDossierProcessesActionsAndRender();
        const { getArticles } = useDossierArticle();
        const { downloadDosDocFile, isFileSupported, postDosDoc } = useDossierDocumentFilesApi();
        const { dosDocs, getDossierDocumentFileById, getDossierDocumentsByCategory } =
            useDossierDocumentsCtx();
        const dossierDocumentsDispatch = useDossierDocumentsCtxDispatch();
        const { getTypeDocuments } = useTypeDocumentsCtx();
        const { setPreviewLoading, documentPreview, setDocumentPreview, setListDocuments } =
            useDocumentPreviewContext();
        const { palette } = useTheme();
        const {
            handleAddFile,
            handleRemoveFile,
            handleAdditionalUploads,
            handleMoveFile,
            handleRemoveDocument,
            getFilePreview,
            getFileExtensionFromFileId,
            submittingItemId,
        } = useDossierFile({
            typeDoc: displayedDocType as
                | typeof DOC_TYPE_REQUIRED
                | typeof DOC_TYPE_INTERNAL
                | typeof DOC_TYPE_DOWNLOADABLE,
            source: 'BACK',
        });

        const isSubmitting = submittingItemId !== null;
        const { modalActions } = useModal();

        // Get displayed doc type list based on query params
        useEffect(() => {
            const urlParams = new URLSearchParams(document.location.search);
            const docType = urlParams.get('docType');
            if (docType) {
                setDisplayedDocType(+docType as DocTypeEnumType);
            }
        }, []);

        const handleSelect = async (
            documentLabel: string,
            fichierId: number,
            documentId: number,
        ) => {
            setPreviewLoading(true);

            try {
                const documentToShow = await getFilePreview(documentId, fichierId);

                if (!isFileSupported(documentToShow.fileType as DocumentExtension)) {
                    if (dosDocs) {
                        const dosDocByFichier = getDossierDocumentFileById(fichierId);
                        if (dosDocByFichier) {
                            await downloadDosDocFile(dosDocByFichier);
                        }
                    }
                }

                setDocumentPreview(documentToShow);
                setPreviewLoading(false);
            } catch (err) {
                console.log(err);
                enqueueSnackbar(
                    `Une erreur est survenue lors de l'affichage du document ${documentLabel}`,
                    { variant: 'error' },
                );
            }
        };

        const showDocument = async (
            documentLabel: string,
            fichierId: number,
            documentId: number,
        ) => {
            const extension = getFileExtensionFromFileId(fichierId);
            if (!isFileSupported(extension as DocumentExtension)) {
                return;
            }

            setPreviewLoading(true);

            try {
                const documentToShow = await getFilePreview(documentId, fichierId);
                setDocumentPreview(documentToShow);
            } catch (err) {
                console.log(err);
                enqueueSnackbar(
                    `Une erreur est survenue lors de l'affichage du document ${documentLabel}`,
                    { variant: 'error' },
                );
            } finally {
                setPreviewLoading(false);
            }
        };

        // Display modal of document types to add
        const handleOpenAddDocumentModal = (doss: DossierOpenSearch) => {
            modalActions.call(
                <DocumentTypeForm
                    title="Ajout de type de document"
                    itemsSelected={[]}
                    type={displayedDocType}
                    handleSelectItems={(documentItems: DocumentTypeItem[]) => {
                        if (documentItems && documentItems.length > 0) {
                            documentItems.map(async (docItem) => {
                                const newDosDoc = await postDosDoc(
                                    doss.id as number,
                                    docItem.id,
                                    docItem.label,
                                    doss.statutId as number,
                                );
                                if (newDosDoc) {
                                    // Maj document in context
                                    dossierDocumentsDispatch({
                                        type: ADD_DOS_DOC_CTX,
                                        payload: newDosDoc,
                                    });
                                    modalActions.close();
                                }
                            });
                        } else {
                            enqueueSnackbar(`Type de document manquant`, {
                                variant: 'error',
                            });
                        }
                    }}
                    getTypeDocuments={getTypeDocuments}
                />,
            );
        };
        // Load list of documents by docType
        useEffect(() => {
            if (dosDocs) {
                let arrayOfDatas: DisplayedDocument[] = [];

                if (displayedDocType === DOC_TYPE_REQUIRED) {
                    arrayOfDatas = [
                        ...getDossierDocumentsByCategory({ categoryId: DOC_TYPE_REQUIRED }),
                        ...getDossierDocumentsByCategory({
                            categoryId: DOC_TYPE_REQUIRED,
                            additionalDocs: true,
                        }),
                    ];
                } else if (displayedDocType === DOC_TYPE_INTERNAL) {
                    arrayOfDatas = [
                        ...getDossierDocumentsByCategory({ categoryId: DOC_TYPE_INTERNAL }),
                        ...getDossierDocumentsByCategory({
                            categoryId: DOC_TYPE_INTERNAL,
                            additionalDocs: true,
                        }),
                    ];
                } else if (displayedDocType === DOC_TYPE_DOWNLOADABLE) {
                    arrayOfDatas = [
                        ...getDossierDocumentsByCategory({ categoryId: DOC_TYPE_DOWNLOADABLE }),
                        ...getDossierDocumentsByCategory({
                            categoryId: DOC_TYPE_DOWNLOADABLE,
                            additionalDocs: true,
                        }),
                    ];
                }

                // DO not display actions on this list
                arrayOfDatas = arrayOfDatas.map((data) => ({
                    ...data,
                    downloadable: false,
                    showable: !!data.fichierId,
                    uploadable: !data.fichierId,
                    smallUploadableBtn: true,
                }));

                setListItems(arrayOfDatas);
                setListDocuments(arrayOfDatas);

                // display the first document with file as preview
                const document = arrayOfDatas.find((doc) => doc.fichierId);
                if (document && !documentPreview) {
                    showDocument(document.label, document.fichierId as number, document.id);
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [displayedDocType, dosDocs]);

        // Load external items (legal announcements) per sibling when DOC_TYPE_DOWNLOADABLE
        useEffect(() => {
            if (displayedDocType !== DOC_TYPE_DOWNLOADABLE || !siblings) {
                return;
            }

            siblings.forEach((doss) => {
                Promise.all([
                    loadLegalAnnoucementsJustifs(doss, CATEGORY_LEGAL_ANNOUCEMENT, [
                        VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
                    ]),
                    getArticles(doss.id as number, CATEGORY_LEGAL_ANNOUCEMENT),
                ])
                    .then(([legalAnnoucements, prestaArticles]) => {
                        const externalDocs: DisplayedDocument[] = [];

                        legalAnnoucements.forEach((legal) => {
                            const validatorProcess = doss.validationProcesses?.find(
                                (process) => process.identifier === legal.id.toString(),
                            );
                            if (validatorProcess) {
                                const prestaArticle = prestaArticles?.find((article) =>
                                    validatorProcess.articles.includes(article.id),
                                );
                                if (prestaArticle) {
                                    externalDocs.push({
                                        dossierId: doss.id as number,
                                        downloadable: false,
                                        fileName: legal.justif,
                                        label: `${validatorProcess.validatorLabel} ${legal.numero}`,
                                        uploadable: false,
                                        id: legal.id,
                                        uploading: false,
                                        editable: false,
                                        showable: true,
                                        signable: false,
                                        canDownloadSigned: false,
                                        removable: false,
                                        removableDoc: false,
                                        hasType: false,
                                        movable: false,
                                        showStatus: false,
                                        showUploadBtn: false,
                                    });
                                }
                            }
                        });

                        setExternalDocsByDossier((prev) => ({
                            ...prev,
                            [doss.id as number]: externalDocs,
                        }));
                    })
                    .catch(() => {});
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [displayedDocType, siblings]);

        let wrapperSx: SxProps = {
            display: displayList ? 'flex' : 'none',
            flexDirection: 'column',
            gap: '8px 0',
            backgroundColor: palette.background.paper,
            borderRightColor: palette.grey[200],
            borderRightWidth: '1px',
            borderRightStyle: 'solid',
            padding: '8px 20px',
            overflowY: 'scroll',
            position: 'absolute',
            zIndex: 100,
            top: '0',
            left: '0',
            width: '40%',
            height: '100%',
        };

        if (currentLayout && currentLayout === LAYOUT_DOCUMENTS_FULL) {
            wrapperSx = { ...wrapperSx, width: '30%', position: 'sticky', overflowY: 'auto' };
        } else {
            wrapperSx = { ...wrapperSx, width: '45%', position: 'sticky', overflowY: 'auto' };
        }
        return (
            <Box sx={wrapperSx}>
                {!dosDocs && (
                    <Box
                        sx={{
                            marginTop: '15px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75em 0',
                        }}
                    >
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                    </Box>
                )}
                {dosDocs &&
                    siblings &&
                    siblings.map((doss) => {
                        const docs = listItems.filter((item) => item.dossierId === doss.id);

                        if (!dosDocs || !dosDocs.length) {
                            return null;
                        }

                        return (
                            <Accordion
                                key={`accordion-${doss.numero}`}
                                title={
                                    <Box sx={{ display: 'flex', gap: '8px' }}>
                                        <Typography
                                            variant="l2dbodym"
                                            sx={{ color: palette.primary.dark }}
                                        >
                                            {doss.numero}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            {doss.formalites.map((formalite) => (
                                                <Tooltip
                                                    title={formalite.libelle}
                                                    arrow
                                                    placement="top"
                                                    key={`${doss.numero}_${formalite.id}`}
                                                >
                                                    <Typography variant="l2dcaption">
                                                        {formalite.libelle.length > 30
                                                            ? `${formalite.libelle.substring(
                                                                  0,
                                                                  25,
                                                              )}[...]`
                                                            : formalite.libelle}
                                                    </Typography>
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    </Box>
                                }
                                expandLabel={null}
                                expandColor={palette.primary}
                                expanded
                            >
                                <>
                                    {displayedDocType === DOC_TYPE_DOWNLOADABLE &&
                                        externalDocsByDossier[doss.id as number]?.length > 0 && (
                                            <DownloadableDocumentsList
                                                documents={externalDocsByDossier[doss.id as number]}
                                                onDownload={() => {}}
                                                onShow={(url) => window.open(url, '_blank')}
                                            />
                                        )}

                                    <RequiredDocumentsList
                                        documents={docs}
                                        onUpload={handleAddFile}
                                        onDeletion={handleRemoveFile}
                                        onDocDeletion={handleRemoveDocument}
                                        onDownload={() => {}}
                                        onShow={handleSelect}
                                        onMove={handleMoveFile}
                                        onClick={() => {}}
                                        isSubmitting={isSubmitting}
                                        submittingItemId={submittingItemId}
                                    />

                                    {displayedDocType && (
                                        <Button
                                            onClick={() => handleOpenAddDocumentModal(doss)}
                                            sx={{
                                                fontWeight: 500,
                                                padding: '8px 20px',
                                                color: palette.primary.dark,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />

                                            <Typography variant="l2dbodys">
                                                Ajouter un document{' '}
                                                {findNameByDocTypeId(displayedDocType)}
                                            </Typography>
                                        </Button>
                                    )}

                                    <RequiredDocumentUploader
                                        uploading={submittingItemId === 'uploader'}
                                        disabled={isSubmitting}
                                        onChange={handleAdditionalUploads}
                                        showUploadBtn={false}
                                    />

                                    {siblings.length > 1 && (
                                        <Box sx={{ margin: '10px 24px' }}>
                                            <Link
                                                to={`/${dossiersBasePath}/${doss.id}`}
                                                target="_blank"
                                                style={{
                                                    textDecoration: 'none',
                                                    color: palette.primary.dark,
                                                    fontSize: '0.9rem',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                                Afficher le dossier {doss.numero}
                                            </Link>
                                        </Box>
                                    )}
                                </>
                            </Accordion>
                        );
                    })}
            </Box>
        );
    };

export default DossierDocumentsValidationList;
